import React from 'react';

import { Xmark } from '@gravity-ui/icons';
import { Button, Icon, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { devicesApi } from 'client/api';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { getMarketDeviceId, MarketDevice, MarketDeviceStatus } from 'client/model/devices';
import { SmartHome } from 'client/model/skill';
import * as commonKeyset from 'i18n/Common';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as keyset from 'i18n/Settings';

import styles from './DeviceLinking.module.scss';

interface DeviceLinkingCardProps {
    aliceApp: SmartHome;
    isDraft: boolean;
    device: MarketDevice;
    afterRemove?: () => void;
    disabled?: boolean;
}

const DELETE_DEVICE_TOAST = 'DELETE_DEVICE_TOAST';

const t = i18n(keyset);
const tCommon = i18n(commonKeyset);
const tGeneral = i18n(generalSettingsKeyset);

export const DeviceLinkingCard: React.FC<DeviceLinkingCardProps> = ({
    aliceApp,
    isDraft,
    device,
    afterRemove,
    disabled,
}) => {
    const { marketId, osku, picture, title, status, vendorName } = device;
    const { add: addToast } = useToaster();
    const deviceId = getMarketDeviceId(device);

    const normalizedOsku = osku?.trim();
    const url = normalizedOsku
        ? `${__appConfig.app.marketFrontentUrl}/card/t/${normalizedOsku}`
        : `${__appConfig.app.marketFrontentUrl}/product/${marketId}`;

    const [deleteDeviceHandler, removeState] = useFetchingHandler({
        handler: () =>
            isDraft
                ? devicesApi.deleteDevice(aliceApp.id, deviceId, aliceApp.channel)
                : devicesApi.deletePublishedDevice(aliceApp.id, deviceId, aliceApp.channel),
        afterLoad: () => afterRemove?.(),
        onError: () => addToast({ title: tGeneral('error'), theme: 'danger', name: DELETE_DEVICE_TOAST }),
    });

    const isNotFound = status === MarketDeviceStatus.NOT_FOUND;
    const isLoading = removeState === 'loading';

    return (
        <div className={styles.deviceCard}>
            <div className={`${styles.deviceCard__logo} ${isNotFound ? styles.deviceCard__logo_hidden : ''}`}>
                {!isNotFound && <img src={picture} className={styles.deviceCard__image} alt={title} />}
            </div>
            <div className={styles.deviceCard__content}>
                <div className={styles.deviceCard__title}>
                    {isNotFound ? (
                        <div className={styles.deviceCard__error}>{t('devices-error-not_found')}</div>
                    ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className={styles.deviceCard__link}>
                            {title}
                        </a>
                    )}
                </div>
                <div>
                    {Boolean(vendorName) && (
                        <>
                            <span className={styles.deviceCard__subtitle}>{vendorName}</span>
                            {'•'}
                        </>
                    )}
                    <span className={styles.deviceCard__subtitle}>ID {deviceId}</span>
                </div>
            </div>
            <div className={styles.deviceCard__remove}>
                <Button
                    title={tCommon('button-delete')}
                    onClick={deleteDeviceHandler}
                    disabled={disabled || isNotFound}
                    loading={isLoading}
                >
                    <Icon data={Xmark} />
                </Button>
            </div>
        </div>
    );
};
